import React, { useEffect, useState, useCallback, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { HistorySection } from "@/components/history/HistorySection";
import { historyApi } from "@/services/history.api";
import { ListButton } from "@refinedev/antd";
import { useGetIdentity } from "@refinedev/core";
import {
  Spin,
  Typography,
  Button,
  Flex,
  Card,
  Tag,
  Select,
  Input,
  Divider,
  Empty,
  Table,
  message,
  theme,
  DatePicker,
  Dropdown,
  Modal,
  Drawer,
  Form,
  Space,
} from "antd";
import {
  HistoryOutlined,
  LeftOutlined,
  EditOutlined,
  EnvironmentOutlined,
  ShopOutlined,
  FileTextOutlined,
  ExportOutlined,
  WhatsAppOutlined,
  MailOutlined,
  HomeOutlined,
  BankOutlined,
  TeamOutlined,
  UserAddOutlined,
  SearchOutlined,
  CloseOutlined,
  IdcardOutlined,
  UserOutlined,
  TrophyOutlined,
  StopOutlined,
  FolderOutlined,
  DislikeOutlined,
  DownOutlined,
  FlagOutlined,
  SyncOutlined,
  DeleteOutlined,
  CopyOutlined,
  FilterOutlined,
} from "@ant-design/icons";
import { InputNumber } from "antd";
import {
  axiosInstance,
  displayEmail,
  formatPrice,
  selectAllOnFocus,
  toHttpError,
} from "@/utilities";
import {
  API_URL_QUOTES,
  API_URL_CONTACTS,
  API_URL_CONTACT_GROUPS,
  API_URL_ORDERS,
  VITE_GOOGLE_MAPS_API_KEY,
  LINK_MARKETPLACE_HASH,
} from "@/services/XCONS_ENV";
import { conversationsApi } from "@/services/conversations.api";
import { quoteShareMessage } from "@/services/quote-share";
import {
  filterOutOperators,
  useOperatorContacts,
} from "@/hooks/useOperatorContacts";
import { SellerIcon, PresupuestoIcon } from "@/components";
import {
  ChannelPickerDropdown,
  channelIcon,
  useWritableChannels,
} from "@/components/channel-picker";
import dayjs from "dayjs";
import { SellerBlock, SellerBlockRef } from "./detail/SellerBlock";
import {
  validateQuoteLevel,
  type QuoteLevelErrors,
} from "./utils/quoteValidations";
import { QuotePermissionsLateral } from "./detail/QuotePermissionsLateral";
import { isAllowedQuoteStateTransition } from "./utils/stateTransitions";
import { resolveOwnerParticipation } from "./utils/resolveOwner";
import { isQuoteViewer } from "./utils/quoteViewer";
import { DeliveryAddressModal } from "./detail/DeliveryAddressModal";
import { BillingDataModal, BILLING_TYPES } from "./detail/BillingDataModal";
import { QuoteAddressBookPopup } from "./detail/QuoteAddressBookPopup";
import { SmartSelectContact } from "@/components";
import mapImagePng from "@/components/icon/images/map.png";
import { useJsApiLoader, Autocomplete } from "@react-google-maps/api";
import { useTranslation } from "react-i18next";
import { isWhatsappFamily } from "@/services/channel-types";

const { Title, Text } = Typography;

const GOOGLE_MAPS_LIBRARIES: "places"[] = ["places"];

const QUOTE_STATES = [
  { value: "quoting", label: "Store", color: "#00C9A7" },
  { value: "customer", label: "Cliente", color: "#9d00c9" },
  { value: "follow", label: "Seguimiento", color: "#ff8800" },
  { value: "buy_on", label: "Comprado", color: "#00A859" },
  { value: "bought_assisted", label: "Comprado asistido", color: "#389e0d" },
  { value: "buy_off", label: "Comprado OFF", color: "#f5222d" },
  { value: "lost", label: "Perdido", color: "#8c8c8c" },
  { value: "archived", label: "Archivado", color: "#595959" },
];

const BOUGHT_STATES = ["buy_on", "bought_assisted", "buy_off"];
// Techo de páginas del historial al buscar el paso a comprado: 10 × 30 = 300 filas.
const PURCHASE_LOOKUP_PAGES = 10;
const PURCHASE_LOOKUP_PAGE = 30;

export const QuoteDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { token } = theme.useToken();
  const { t } = useTranslation();

  const [loading, setLoading] = useState(true);
  const [quote, setQuote] = useState<any>(null);
  const [sellers, setSellers] = useState<any[]>([]);
  const [editingName, setEditingName] = useState(false);
  const [projectName, setProjectName] = useState("");
  const [deliveryModalOpen, setDeliveryModalOpen] = useState(false);
  const [billingModalOpen, setBillingModalOpen] = useState(false);
  const [addressBookType, setAddressBookType] = useState<"shipping" | "billing" | null>(null);
  const [ownerModalOpen, setOwnerModalOpen] = useState(false);
  const [ownerGroups, setOwnerGroups] = useState<any[]>([]);
  const [ownerContactCompany, setOwnerContactCompany] = useState<string | undefined>(undefined);
  const [ownerContactVatId, setOwnerContactVatId] = useState<string | undefined>(undefined);
  const [quoteAddresses, setQuoteAddresses] = useState<{ shipping: any; billing: any }>({ shipping: null, billing: null });
  const [historySearch, setHistorySearch] = useState("");
  const [historyQ, setHistoryQ] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState<any[]>([]);
  const [searchOpen, setSearchOpen] = useState(false);
  const [iconHovered, setIconHovered] = useState(false);
  const [searchLoading, setSearchLoading] = useState(false);
  const sellerRefs = useRef<Record<string, SellerBlockRef | null>>({});
  const [addQtyMap, setAddQtyMap] = useState<Record<string, number>>({});
  const [submittedSellers, setSubmittedSellers] = useState<Set<string>>(
    () => new Set(),
  );
  const [selectedVendor, setSelectedVendor] = useState<Record<string, string>>({});
  const [lastUsedVendor, setLastUsedVendor] = useState<string | null>(null);
  const searchTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [inlineAddressValue, setInlineAddressValue] = useState("");
  const [inlineAddressSaving, setInlineAddressSaving] = useState(false);
  const [expiring, setExpiring] = useState(false);
  const [duplicating, setDuplicating] = useState(false);
  const [contactChannels, setContactChannels] = useState<{ whatsapp: string | null; email: string | null }>({ whatsapp: null, email: null });
  const inlineAutocompleteRef = useRef<google.maps.places.Autocomplete | null>(null);

  const { isLoaded: isMapsLoaded } = useJsApiLoader({
    id: "google-map-script",
    googleMapsApiKey: VITE_GOOGLE_MAPS_API_KEY,
    libraries: GOOGLE_MAPS_LIBRARIES,
    language: "es",
    region: "AR",
    version: "weekly",
  });

  const loadQuote = useCallback(async () => {
    try {
      setLoading(true);
      const [quoteRes] = await Promise.all([
        axiosInstance.get(`${API_URL_QUOTES}/${id}`),
      ]);
      setQuote(quoteRes.data);
      setSellers(quoteRes.data.quoteSellers || []);
      setProjectName(quoteRes.data.name || "");
    } catch {
      message.error("Error al cargar el presupuesto");
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    if (id) loadQuote();
  }, [id, loadQuote]);

  // La fecha de compra es `lastBoughtAt` del presupuesto. Si viene vacía, sale del
  // historial: el último paso a cualquiera de los estados de comprado (el timeline viene
  // del más nuevo al más viejo).
  const currentSellerState: string | undefined = sellers[0]?.state;
  const [purchasedAt, setPurchasedAt] = useState<string | null>(null);
  useEffect(() => {
    if (!id || !currentSellerState || !BOUGHT_STATES.includes(currentSellerState)) {
      setPurchasedAt(null);
      return;
    }
    if (quote?.lastBoughtAt) {
      setPurchasedAt(quote.lastBoughtAt);
      return;
    }
    let cancelled = false;
    (async () => {
      let before: string | undefined;
      try {
        for (let i = 0; i < PURCHASE_LOOKUP_PAGES; i++) {
          const page = await historyApi.timeline("quote", id, {
            before,
            limit: PURCHASE_LOOKUP_PAGE,
          });
          const hit = page.find(
            (r) =>
              r.kind === "event" &&
              r.payload.verb === "changed_status" &&
              BOUGHT_STATES.includes(String(r.payload.metadata?.state ?? "")),
          );
          if (hit) {
            if (!cancelled) setPurchasedAt(hit.occurredAt);
            return;
          }
          if (page.length < PURCHASE_LOOKUP_PAGE) break;
          before = page[page.length - 1].occurredAt;
        }
      } catch {
        // Sin historial no hay fecha que mostrar; el resto del detalle sigue igual.
      }
      if (!cancelled) setPurchasedAt(null);
    })();
    return () => {
      cancelled = true;
    };
  }, [id, currentSellerState, quote?.updatedAt, quote?.lastBoughtAt]);

  useEffect(() => {
    const t = setTimeout(() => setHistoryQ(historySearch.trim()), 350);
    return () => clearTimeout(t);
  }, [historySearch]);

  useEffect(() => {
    if (!quote) return;
    const owner = (quote.participations ?? []).find(
      (p: any) => (p.participationType ?? "") === "",
    );
    const contactId = owner?.contactId;
    const storeCode = quote.store?.code;
    if (!contactId || !storeCode) return;
    let active = true;
    conversationsApi
      .listConversations({ contactIds: String(contactId), storeCode })
      .then((convs) => {
        if (!active) return;
        const wa = convs.find((c) => isWhatsappFamily(c.channel_type));
        const em = convs.find((c) => c.channel_type === "email");
        setContactChannels({ whatsapp: wa?.channel_name ?? null, email: em?.channel_name ?? null });
      })
      .catch(() => {
        if (!active) return;
        setContactChannels({ whatsapp: null, email: null });
      });
    return () => { active = false; };
  }, [quote]);

  // Technical-operator filter: hide the storefront service account from any
  // participation-derived UI. Operators are added by the Magento adminLogin
  // flow and never represent real participants in the admin view.
  const operatorContactIds = useOperatorContacts();
  const { data: identity } = useGetIdentity<{ id?: number; permissions?: any }>();
  const visibleParticipations = filterOutOperators(
    quote?.participations,
    operatorContactIds,
  );

  // Derive owner = customer participation (participationType="") with the
  // lowest position. See resolveOwnerParticipation for why position 0 must not
  // be required (Magento guest→login assigns the owner at a non-zero position).
  const owner: any = resolveOwnerParticipation(visibleParticipations);
  const ownerUserId: number | null = owner?.userId ?? null;
  // Compartir por WhatsApp (N6): antes de ir a Conversaciones hay que saber por qué canal.
  // Compartir por WhatsApp (P9): el canal se elige en un desplegable, igual que el "+" de
  // Conversaciones. Los canales se piden recién al abrirlo. Con el teléfono del cliente, porque un
  // canal de la Cloud API se ofrece sólo si ese número tiene la ventana de 24 h abierta (D1).
  const shareChannels = useWritableChannels(quote?.store?.code, owner?.whatsapp);

  // Cantidad de pedidos del owner (customer) en el store del presupuesto,
  // para el header. Se resuelve vía el endpoint reutilizable de orders.
  const [ownerOrdersCount, setOwnerOrdersCount] = useState<number>(0);
  const [ownerFirstOrderDate, setOwnerFirstOrderDate] = useState<string | null>(null);
  useEffect(() => {
    if (!ownerUserId) {
      setOwnerOrdersCount(0);
      setOwnerFirstOrderDate(null);
      return;
    }
    const storeId =
      quote?.storeId ?? quote?.store?.store_id ?? quote?.store?.storeId;
    const params: Record<string, number> = { userId: ownerUserId };
    if (storeId) params.storeId = Number(storeId);
    Promise.all([
      axiosInstance.get(`${API_URL_ORDERS}/customer/order-stats`, { params }),
      axiosInstance.get(`${API_URL_ORDERS}`, {
        params: {
          "filter.customerId": `$eq:${ownerUserId}`,
          limit: 1,
          "sortBy": "createdAt:ASC",
        },
      }),
    ])
      .then(([statsRes, firstRes]) => {
        setOwnerOrdersCount(Number(statsRes.data?.count ?? statsRes.data?.total ?? 0));
        const fromStats =
          statsRes.data?.firstOrderDate ??
          statsRes.data?.first_order_date ??
          statsRes.data?.firstOrder ??
          null;
        const fromList =
          (firstRes.data?.data ?? firstRes.data)?.[0]?.createdAt ?? null;
        setOwnerFirstOrderDate(fromStats ?? fromList);
      })
      .catch(() => {
        setOwnerOrdersCount(0);
        setOwnerFirstOrderDate(null);
      });
  }, [ownerUserId, quote?.storeId, quote?.store?.store_id, quote?.store?.storeId]);

  // Load company from contact profile
  useEffect(() => {
    if (!owner?.contactId) {
      setOwnerContactCompany(undefined);
      setOwnerContactVatId(undefined);
      return;
    }
    axiosInstance
      .get(`${API_URL_CONTACTS}`, {
        params: { "filter.contactId": owner.contactId },
      })
      .then(({ data }) => {
        const contact = (data?.data ?? [])[0];
        const company = contact?.user?.company ?? contact?.company ?? undefined;
        setOwnerContactCompany(company || undefined);
        setOwnerContactVatId(contact?.billingAddress?.vatId || undefined);
      })
      .catch(() => { setOwnerContactCompany(undefined); setOwnerContactVatId(undefined); });
  }, [owner?.contactId]);

  // Load contact groups for the owner
  useEffect(() => {
    if (!owner?.contactId) {
      setOwnerGroups([]);
      return;
    }
    axiosInstance
      .get(`${API_URL_CONTACT_GROUPS}`, {
        params: { "filter.contactGroupRelation.contactId": owner.contactId },
      })
      .then(({ data }) => {
        setOwnerGroups(data?.data || []);
      })
      .catch(() => {
        setOwnerGroups([]);
      });
  }, [owner?.contactId]);

  // Load address snapshots
  useEffect(() => {
    if (!id) return;
    axiosInstance
      .get(`${API_URL_QUOTES}/${id}/addresses`)
      .then(({ data }) => {
        setQuoteAddresses({ shipping: data?.shipping || null, billing: data?.billing || null });
      })
      .catch(() => {
        setQuoteAddresses({ shipping: null, billing: null });
      });
  }, [id, quote]);

  // Sync inline input with current address snapshot
  useEffect(() => {
    const shipping = quoteAddresses.shipping;
    const parts = [
      shipping?.street1,
      shipping?.street2,
      shipping?.cityName || quote?.city,
      shipping?.provinceName || quote?.region,
      shipping?.postcode,
    ].filter(Boolean);
    setInlineAddressValue(parts.length > 0 ? parts.join(", ") : "");
  }, [quoteAddresses.shipping, quote?.city, quote?.region]);

  const updateQuote = async (patch: Record<string, any>) => {
    try {
      await axiosInstance.patch(`${API_URL_QUOTES}/${id}`, patch);
    } catch {
      message.error("Error al actualizar el presupuesto");
    }
  };

  const handleNameSave = async () => {
    setEditingName(false);
    closeSearch();
    await updateQuote({ name: projectName });
  };

  // Debounce: search 1 second after last keystroke
  useEffect(() => {
    if (!searchQuery.trim()) {
      setSearchResults([]);
      setSearchOpen(false);
      setAddQtyMap({});
      return;
    }
    if (searchTimer.current) clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => {
      handleProductSearch();
    }, 200);
    return () => {
      if (searchTimer.current) clearTimeout(searchTimer.current);
    };
  }, [searchQuery]);

  const handleProductSearch = async () => {
    if (!searchQuery.trim()) return;
    setSearchLoading(true);
    try {
      const { data } = await axiosInstance.get(
        `${API_URL_QUOTES}/${id}/products/search?q=${encodeURIComponent(searchQuery)}&limit=5`
      );
      const results = data || [];
      setSearchResults(results);
      setSearchOpen(true);
      // Pre-select vendor for each result. Prefer the last seller the operator
      // used (so consecutive adds keep the same seller selected); fall back to
      // the first vendor offering the product.
      const preSelected: Record<string, string> = {};
      results.forEach((p: any) => {
        const vendors: string[] = p.vendors || [];
        if (vendors.length === 0) return;
        if (lastUsedVendor && vendors.includes(lastUsedVendor)) {
          preSelected[p.objectID] = lastUsedVendor;
        } else {
          preSelected[p.objectID] = vendors[0];
        }
      });
      setSelectedVendor((prev) => ({ ...prev, ...preSelected }));
    } catch {
      message.error("Error al buscar productos");
    } finally {
      setSearchLoading(false);
    }
  };

  const handleAddProduct = async (product: any) => {
    const vendor = selectedVendor[product.objectID];
    if (!vendor) {
      message.warning("Seleccioná un seller para agregar el producto");
      return;
    }
    const qty = addQtyMap[product.objectID] || 1;
    const price = product.priceByVendor?.[vendor] || product.price || 0;

    // Pick the destination DG. Convention: the operator explicitly sets the
    // "active" DG via the switch in each delivery header (and the backend
    // auto-updates it after a move). If nothing is set yet, fall back to the
    // last (max) DG — matches the prior default.
    const sellerData = sellers.find((s: any) => s.sellerCode === vendor);
    const sellerItems: any[] = sellerData?.quoteSellerItems || [];
    let emptyGroups: number[] = [];
    let activeDg: number | null = null;
    try {
      const rawAd = sellerData?.additionalData;
      const ad = typeof rawAd === "string" ? JSON.parse(rawAd) : rawAd || {};
      if (Array.isArray(ad.emptyDeliveryGroups)) {
        emptyGroups = ad.emptyDeliveryGroups.map((g: any) => Number(g) || 0);
      }
      if (typeof ad.activeDeliveryGroup === "number" && ad.activeDeliveryGroup > 0) {
        activeDg = ad.activeDeliveryGroup;
      }
    } catch {
      emptyGroups = [];
    }
    const itemMax = sellerItems.reduce(
      (max: number, item: any) => Math.max(max, item.deliveryGroup || 1),
      1,
    );
    const emptyMax = emptyGroups.reduce((max, g) => Math.max(max, g), 0);
    const maxDg = Math.max(itemMax, emptyMax);
    const targetDg = activeDg ?? maxDg;

    try {
      await axiosInstance.post(
        `${API_URL_QUOTES}/${id}/sellers/${vendor}/items`,
        {
          magentoProductId: parseInt(product.objectID) || 0,
          qty,
          price,
          deliveryGroup: targetDg,
        }
      );
      message.success("Producto agregado");
      setLastUsedVendor(vendor);
      // B2: reset qty input for this product after backend confirmation.
      // Keep the search panel open so the operator can keep adding.
      setAddQtyMap((prev) => {
        const next = { ...prev };
        delete next[product.objectID];
        return next;
      });
      const sellerRef = sellerRefs.current[vendor];
      if (sellerRef) {
        await sellerRef.reload();
      } else {
        loadQuote();
      }
    } catch {
      message.error("Error al agregar producto");
    }
  };

  // B3: close the product search whenever the operator performs an action
  // that isn't "add a product". Adding a product preserves the panel.
  const closeSearch = () => {
    setSearchQuery("");
    setSearchOpen(false);
    setSearchResults([]);
    setAddQtyMap({});
  };

  /**
   * Vencimiento instantáneo: le pide al API que expire el presupuesto, vuelva a
   * pedirle los precios al catálogo y renueve la validez — lo mismo que pasa en
   * Magento cuando el customer abre un presupuesto vencido, pero a pedido.
   *
   * Recarga todo después: los precios los renderiza cada `SellerBlock` con su
   * propio fetch, así que sin el `reload()` de cada uno la pantalla seguiría
   * mostrando los importes viejos.
   */
  const forceExpiration = async () => {
    closeSearch();
    setExpiring(true);
    try {
      const { data } = await axiosInstance.post(`${API_URL_QUOTES}/${id}/expire`);
      const repriced = data?.repriced ?? 0;
      const skipped = data?.skipped ?? 0;
      if (skipped > 0) {
        message.warning(
          `${repriced} precio(s) actualizado(s). ${skipped} ítem(s) sin precio en el catálogo: quedaron con el anterior.`,
        );
      } else {
        message.success(`Vencimiento simulado — ${repriced} precio(s) actualizado(s)`);
      }
      await Promise.all(
        Object.values(sellerRefs.current).map((ref) => ref?.reload?.()),
      );
      loadQuote();
    } catch {
      message.error("No se pudo vencer el presupuesto");
    } finally {
      setExpiring(false);
    }
  };

  /**
   * Duplicar: el presupuesto nuevo se lleva productos, cantidades y seller, pero
   * nace limpio — sin dueño, sin direcciones, sin medio de pago, en el estado
   * inicial y con validez nueva. Se navega al duplicado porque es sobre ése que
   * el operador va a seguir trabajando.
   */
  const handleDuplicate = async () => {
    closeSearch();
    setDuplicating(true);
    try {
      const { data } = await axiosInstance.post(`${API_URL_QUOTES}/${id}/duplicate`);
      if (!data?.quoteId) {
        message.error("No se pudo duplicar el presupuesto");
        return;
      }
      message.success(`Presupuesto duplicado — #${data.quoteId}`);
      navigate(`/quotes/${data.quoteId}`);
    } catch (error) {
      message.error(toHttpError(error).message);
    } finally {
      setDuplicating(false);
    }
  };

  /**
   * Eliminar: no tiene vuelta atrás, así que va con confirmación y nombrando el
   * presupuesto. El API rechaza los que tienen una orden asociada; ese mensaje
   * (que trae el número de orden) se muestra tal cual.
   */
  const promptDelete = () => {
    Modal.confirm({
      title: `¿Eliminar el presupuesto #${id}?`,
      content:
        "Se elimina para siempre, junto con sus productos. Esta acción no se puede deshacer.",
      okText: "Eliminar",
      okButtonProps: { danger: true },
      cancelText: "Cancelar",
      onOk: async () => {
        try {
          await axiosInstance.delete(`${API_URL_QUOTES}/${id}`);
          message.success(`Presupuesto #${id} eliminado`);
          navigate("/quotes");
        } catch (error) {
          message.error(toHttpError(error).message);
          // Se re-lanza para que antd deje el modal abierto: el caso típico es
          // "tiene una orden asociada", y conviene que el operador lea por qué.
          throw error;
        }
      },
    });
  };

  const promptForceExpiration = () => {
    Modal.confirm({
      title: "¿Vencer el presupuesto ahora?",
      content:
        "Se le vuelven a pedir los precios al catálogo y se reescriben los del presupuesto. La validez arranca de nuevo desde ahora.",
      okText: "Vencer y actualizar precios",
      cancelText: "Cancelar",
      onOk: forceExpiration,
    });
  };

  const handleStateChange = async (sellerCode: string, newState: string) => {
    try {
      await axiosInstance.patch(
        `${API_URL_QUOTES}/${id}/sellers/${sellerCode}/state`,
        { state: newState }
      );
      await loadQuote();
    } catch {
      message.error("Error al cambiar estado");
    }
  };

  if (loading) {
    return (
      <Flex justify="center" align="center" style={{ minHeight: 400 }}>
        <Spin size="large" />
      </Flex>
    );
  }

  if (!quote) {
    return <Empty description="Presupuesto no encontrado" />;
  }

  // Resolve owner = customer participation (participationType="") with the
  // lowest position — same canonical convention as the API and the quotes list.
  const ownerParticipation: any = resolveOwnerParticipation(visibleParticipations);
  const customerName = ownerParticipation
    ? `${ownerParticipation.firstname || ""} ${ownerParticipation.lastname || ""}`.trim()
    : null;
  // Viewer (por Store o por participación): ve los inputs de la tabla deshabilitados, pero
  // puede confirmar el pedido. xcons/xcons-docs#488. Se usan las participaciones crudas: las
  // del operador logueado son justo las que interesan acá.
  const quoteReadOnly = isQuoteViewer({
    storeQuoteLevel: quote.store?.code
      ? identity?.permissions?.store?.quote?.[quote.store.code]
      : null,
    participations: quote.participations,
    userId: identity?.id,
  });
  // El placeholder del alta por WhatsApp no se muestra: sin e-mail el bloque entero no se
  // renderiza, que es lo que ya pasaba con un cliente sin casilla.
  const customerEmail = displayEmail(ownerParticipation?.email);
  const customerWhatsapp = ownerParticipation?.whatsapp;

  /**
   * Elegido el canal, se abre Conversaciones en un tab nuevo con todo resuelto —canal, contacto y el
   * mensaje ya escrito—. Sólo falta apretar enviar.
   *
   * El texto sale de `quoteShareMessage` para que sea el mismo que manda el panel de contexto del
   * chat (P3): son dos entradas al mismo mensaje.
   */
  const shareQuoteByWhatsapp = (channelId: string) => {
    const draft = quoteShareMessage({
      quoteId: quote.id,
      storeName: quote.store?.name,
      storeLink: quote.store?.link,
      shareHash: quote.shareHash,
    });
    if (!draft) {
      message.error("Falta el link del Store o el hash del presupuesto");
      return;
    }
    const params = new URLSearchParams({
      newMsg: "whatsapp",
      channelId,
      phone: customerWhatsapp ?? "",
      draft,
    });
    if (owner?.contactId) params.set("contactId", String(owner.contactId));
    window.open(`/conversations?${params.toString()}`, "_blank");
  };
  const openConversationFromQuote = (channelId: string) => {
    const params = new URLSearchParams({ newMsg: "msg", channelId });
    if (ownerContactId) params.set("contactId", String(ownerContactId));
    if (customerName) params.set("contactName", customerName);
    if (customerWhatsapp) params.set("whatsapp", customerWhatsapp);
    if (customerEmail) params.set("email", customerEmail);
    if (quote?.store?.code) params.set("storeCode", quote.store.code);
    navigate(`/conversations?${params.toString()}`);
  };

  const customerCompany = (
    ownerParticipation?.company ??
    ownerParticipation?.user?.company ??
    ownerParticipation?.contact?.user?.company ??
    ownerParticipation?.contact?.company
  ) as string | undefined;
  const ownerContactId = ownerParticipation?.contactId;

  // Quote-level validations for the "Confirmar pedido" flow (owner + addresses).
  // Shown only after at least one seller's Confirm button was clicked.
  const submitAttempted = submittedSellers.size > 0;
  const quoteLevelErrors: QuoteLevelErrors = validateQuoteLevel({
    owner: ownerParticipation
      ? {
          contactId:
            ownerParticipation.contactId != null
              ? Number(ownerParticipation.contactId)
              : undefined,
          userId: ownerParticipation.userId,
        }
      : null,
    shippingAddr: quoteAddresses.shipping,
    billingAddr: quoteAddresses.billing,
  });
  const registerSubmitAttempt = (sellerCode: string) => {
    setSubmittedSellers((prev) => {
      if (prev.has(sellerCode)) return prev;
      const next = new Set(prev);
      next.add(sellerCode);
      return next;
    });
  };
  const ERROR_RED = "#dc3545";
  const ERROR_BG = "#fff1f0";

  // Group participations by type for info card. Order by `position` ascending
  // so the level owner (position=0) renders first.
  const sortByPosition = (a: any, b: any) =>
    (Number(a.position) || 0) - (Number(b.position) || 0);
  const storeParticipations = visibleParticipations
    .filter((p: any) => p.participationType === "store")
    .sort(sortByPosition);
  const sellerParticipations = visibleParticipations
    .filter((p: any) => p.participationType === "seller")
    .sort(sortByPosition);

  const prioritizedState =
    sellers.length > 0 ? sellers[0].state || "quoting" : "quoting";

  // Map for delivery address display — prefer address snapshot, fallback to quote legacy fields
  const shippingAddr = quoteAddresses.shipping;
  const addrLat = shippingAddr?.lat || quote.latitude;
  const addrLng = shippingAddr?.lng || quote.longitude;
  const hasCoords = addrLat && addrLng;
  const mapSrc = hasCoords
    ? `https://maps.googleapis.com/maps/api/staticmap?center=${addrLat},${addrLng}&zoom=15&size=400x120&markers=${addrLat},${addrLng}&key=${VITE_GOOGLE_MAPS_API_KEY}`
    : mapImagePng;

  // Build address display text from snapshot
  const addressParts = [
    shippingAddr?.street1,
    shippingAddr?.street2,
    shippingAddr?.cityName || quote.city,
    shippingAddr?.provinceName || quote.region,
    shippingAddr?.postcode,
  ].filter(Boolean);
  const addressText = addressParts.length > 0 ? addressParts.join(", ") : "Sin dirección configurada";

  // Billing card display
  const billingAddr = quoteAddresses.billing;
  const billingLetter = billingAddr?.billingType
    ? (BILLING_TYPES.find((t) => t.value === String(billingAddr.billingType))?.label || "").charAt(0)
    : "";
  const billingName = billingAddr
    ? billingAddr.company || `${billingAddr.firstname || ""} ${billingAddr.lastname || ""}`.trim()
    : "";
  const billingCompanyLine = billingAddr
    ? [billingName, billingAddr.vatId].filter(Boolean).join(" ")
    : "";
  const billingAddressLine = billingAddr
    ? [billingAddr.street1, billingAddr.cityName].filter(Boolean).join(", ")
    : "";

  const onInlineAutocompleteLoad = (autocomplete: google.maps.places.Autocomplete) => {
    if (!window.google) return;
    const bounds = new window.google.maps.LatLngBounds(
      new window.google.maps.LatLng(-55.174989, -73.563892),
      new window.google.maps.LatLng(-21.770833, -53.984375),
    );
    autocomplete.setBounds(bounds);
    autocomplete.setComponentRestrictions({ country: ["AR"] });
    inlineAutocompleteRef.current = autocomplete;
    autocomplete.addListener("place_changed", () => {
      const place = autocomplete.getPlace();
      if (place?.address_components && place.geometry?.location) {
        void handleInlinePlaceSelect(place);
      }
    });
  };

  const handleInlinePlaceSelect = async (place: google.maps.places.PlaceResult) => {
    if (!place.geometry?.location) return;
    const lat = place.geometry.location.lat().toString();
    const lng = place.geometry.location.lng().toString();
    const components = place.address_components || [];
    const getComponent = (type: string) =>
      components.find((c) => c.types.includes(type))?.long_name;

    const newStreet1 =
      [getComponent("route"), getComponent("street_number")].filter(Boolean).join(" ") ||
      place.formatted_address ||
      "";
    const newCity =
      getComponent("locality") || getComponent("administrative_area_level_2") || "";
    const newProvince = getComponent("administrative_area_level_1") || "";
    const newPostcode = getComponent("postal_code") || "";

    try {
      setInlineAddressSaving(true);
      await axiosInstance.patch(`${API_URL_QUOTES}/${id}/addresses`, {
        type: "shipping",
        firstname: shippingAddr?.firstname || "",
        lastname: shippingAddr?.lastname || "",
        phone: shippingAddr?.phone || "",
        street1: newStreet1,
        street2: shippingAddr?.street2 || "",
        cityName: newCity,
        provinceName: newProvince,
        postcode: newPostcode,
        reference: shippingAddr?.reference || "",
        lat,
        lng,
      });
      message.success("Dirección actualizada");
      loadQuote();
    } catch {
      message.error("Error al guardar dirección");
    } finally {
      setInlineAddressSaving(false);
    }
  };

  return (
    <div style={{ width: "100%", padding: "0 16px" }}>
      {/* ===== HEADER ===== */}
      <Flex
        align="center"
        justify="space-between"
        style={{ marginBottom: 12, marginTop: 8 }}
      >
        <Flex align="center" gap={12}>
          <ListButton
            onClick={() => navigate("/quotes")}
            hideText
            icon={<LeftOutlined />}
          />
          <Title
            level={4}
            style={{
              margin: 0,
              color: submitAttempted && quoteLevelErrors.owner ? ERROR_RED : undefined,
            }}
          >
            #{quote.id}
          </Title>
          {customerName ? (
            <Text
              strong
              style={{
                fontSize: 18,
                color: "#15814b",
                cursor: ownerContactId ? "pointer" : "default",
              }}
              onClick={() => {
                if (ownerContactId) navigate(`/contacts/${ownerContactId}`);
              }}
            >
              {customerName}
            </Text>
          ) : (
            <>
              <Text
                strong
                style={{
                  fontSize: 18,
                  color: submitAttempted && quoteLevelErrors.owner ? ERROR_RED : undefined,
                }}
              >
                {submitAttempted && quoteLevelErrors.owner
                  ? t("quotes.header.ownerless")
                  : "Sin dueño"}
              </Text>
              {submitAttempted && quoteLevelErrors.owner && (
                <Text
                  data-error="true"
                  style={{ color: ERROR_RED, fontSize: 13 }}
                >
                  {t(quoteLevelErrors.owner)}
                </Text>
              )}
              <Button
                type="primary"
                size="small"
                icon={<UserAddOutlined />}
                onClick={() => setOwnerModalOpen(true)}
              >
                Agregar
              </Button>
            </>
          )}
        </Flex>
        <QuotePermissionsLateral
          quoteId={Number(id)}
          storeName={quote.store?.name || ""}
          storeCode={quote.store?.code || ""}
          storeLink={quote.store?.link || ""}
          shareHash={quote.shareHash || ""}
          accessLevel={quote.accessLevel}
          initialParticipations={quote.participations || []}
          onChange={() => {
            closeSearch();
            loadQuote();
          }}
        />
      </Flex>

      {/* ===== OWNER INFO CARD ===== */}
      {customerName && (
        <Card
          size="small"
          style={{
            marginBottom: 16,
            borderColor: token.colorBorderSecondary,
          }}
          styles={{ body: { padding: "14px 20px" } }}
        >
          <Flex align="center" justify="space-between" style={{ width: "100%" }} gap={12}>
            {/* Left: contact info en dos sub-columnas */}
            <Flex gap={16} align="center">
              {/* Sub-col 1: teléfono + email */}
              <Flex vertical gap={6}>
                {customerWhatsapp && (
                  <Flex align="center" gap={8}>
                    <WhatsAppOutlined style={{ fontSize: 14, color: "#15744b" }} />
                    <ChannelPickerDropdown
                      channels={shareChannels.channels}
                      loading={shareChannels.loading}
                      failed={shareChannels.failed}
                      unavailable={shareChannels.unavailable}
                      onOpen={shareChannels.load}
                      placement="bottomLeft"
                      emptyText="No hay canales disponibles para este store"
                      onPick={openConversationFromQuote}
                      onAddChannel={() => navigate(`/stores/channels/${quote?.store?.code}`)}
                      extraItems={[{
                        key: "__wa_personal",
                        label: <Flex align="center" gap={8} style={{ padding: "4px 0", minWidth: 220 }}><WhatsAppOutlined style={{ fontSize: 16 }} /><span>WhatsApp Web personal</span></Flex>,
                        onClick: () => {
                          const msg = quoteShareMessage({ quoteId: quote.id, storeName: quote.store?.name, storeLink: quote.store?.link, shareHash: quote.shareHash });
                          const phone = customerWhatsapp.replace(/[^\d]/g, "");
                          window.open(msg ? `https://wa.me/${phone}?text=${encodeURIComponent(msg)}` : `https://wa.me/${phone}`, "_blank");
                        },
                      }]}
                    >
                      <Text strong style={{ fontSize: 13, color: "#15744b", cursor: "pointer" }}>
                        {customerWhatsapp}
                      </Text>
                    </ChannelPickerDropdown>
                  </Flex>
                )}
                {customerEmail && (
                  <Flex align="center" gap={8}>
                    <MailOutlined style={{ fontSize: 14, color: "#15744b" }} />
                    <ChannelPickerDropdown
                      channels={shareChannels.channels}
                      loading={shareChannels.loading}
                      failed={shareChannels.failed}
                      unavailable={shareChannels.unavailable}
                      onOpen={shareChannels.load}
                      placement="bottomLeft"
                      emptyText="No hay canales disponibles para este store"
                      onPick={openConversationFromQuote}
                      onAddChannel={() => navigate(`/stores/channels/${quote?.store?.code}`)}
                      extraItems={[{
                        key: "__email_personal",
                        label: <Flex align="center" gap={8} style={{ padding: "4px 0", minWidth: 220 }}>{channelIcon("email")}<span>email personal</span></Flex>,
                        onClick: () => {
                          const msg = quoteShareMessage({ quoteId: quote.id, storeName: quote.store?.name, storeLink: quote.store?.link, shareHash: quote.shareHash });
                          window.location.href = msg ? `mailto:${customerEmail}?body=${encodeURIComponent(msg)}` : `mailto:${customerEmail}`;
                        },
                      }]}
                    >
                      <Text strong style={{ fontSize: 13, color: "#15744b", cursor: "pointer" }}>
                        {customerEmail}
                      </Text>
                    </ChannelPickerDropdown>
                  </Flex>
                )}
              </Flex>

              {/* Sub-col 2: orders since + empresa */}
              <Flex vertical gap={6}>
                <Flex align="center" gap={4}>
                  <HistoryOutlined style={{ fontSize: 14, color: "#15744b" }} />
                  <Text
                    strong
                    style={{ fontSize: 14, color: "#15744b", cursor: ownerUserId ? "pointer" : "default" }}
                    onClick={() => {
                      if (!ownerUserId) return;
                      navigate(`/orders?filters[0][field]=customerId&filters[0][operator]=eq&filters[0][value]=${ownerUserId}`);
                    }}
                  >
                    {ownerOrdersCount} pedidos{ownerFirstOrderDate ? ` desde ${dayjs(ownerFirstOrderDate).format("DD/MM/YY")}` : ""}
                  </Text>
                </Flex>
                <Flex align="center" gap={4}>
                  <FlagOutlined style={{ fontSize: 14 }} />
                  <Text style={{ fontSize: 14, color: "rgba(0,0,0,0.88)" }}>
                    {ownerContactCompany || customerCompany || "—"}
                  </Text>
                </Flex>
              </Flex>
            </Flex>

            <Divider type="vertical" style={{ height: 50, margin: "0 4px" }} />

            {/* Right: grupos del cliente */}
            <Flex
              gap={6}
              wrap="wrap"
              align="center"
              justify="flex-end"
              style={{ flex: 1 }}
            >
              {ownerGroups.map((g: any, i: number) => (
                <Tag
                  key={g.contactGroupId || i}
                  icon={<TeamOutlined style={{ fontSize: 14 }} />}
                  style={{ fontSize: 12, height: 24, padding: "0 4px", borderRadius: 4, background: "rgba(0,0,0,0.06)", border: "1px solid rgba(0,0,0,0)", color: "rgba(0,0,0,0.65)", display: "inline-flex", alignItems: "center", gap: 4 }}
                >
                  {g.name}
                </Tag>
              ))}
            </Flex>
          </Flex>
        </Card>
      )}

      {/* ===== INFO + STATUS ROW ===== */}
      <Flex gap={16} style={{ marginBottom: 16 }} wrap="wrap">
        {/* Datos del presupuesto */}
        <Card
          size="small"
          style={{ flex: "1 1 320px", borderColor: token.colorBorderSecondary }}
          styles={{ body: { padding: "18px 20px" } }}
        >
          <Flex vertical gap={14}>
            {/* Top row: icon (left) + date (right) */}
            <Flex align="center" justify="space-between" gap={16}>
              <div
                onMouseEnter={() => setIconHovered(true)}
                onMouseLeave={() => setIconHovered(false)}
                onClick={() => {
                  if (!quote.shareHash || !quote.store?.link) {
                    message.error("Falta el link del Store o el hash del presupuesto");
                    return;
                  }
                  const link = `${quote.store.link}${LINK_MARKETPLACE_HASH}${quote.shareHash}`;
                  window.open(link, "_blank");
                }}
                style={{
                  position: "relative",
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  borderRadius: 10,
                  padding: 6,
                  background: iconHovered ? "rgba(242, 180, 17, 0.15)" : "transparent",
                  transition: "background 0.2s",
                  cursor: "pointer",
                }}
              >
                <PresupuestoIcon width={56} height={56} style={{ color: "#f2b411", opacity: iconHovered ? 0.3 : 1, transition: "opacity 0.2s" }} />
                {iconHovered && (
                  <span
                    style={{
                      position: "absolute",
                      inset: 0,
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "center",
                      fontSize: 12,
                      fontWeight: 700,
                      color: "#d48806",
                      letterSpacing: 0.3,
                      lineHeight: 1.2,
                      pointerEvents: "none",
                    }}
                  >
                    <span>Vista</span>
                    <span>cliente</span>
                  </span>
                )}
              </div>
              <Flex vertical align="flex-end">
                <Title level={3} style={{ margin: 0, lineHeight: 1.1 }}>
                  {dayjs(quote.createdAt).format("DD/MM/YY")}
                </Title>
                <Text type="secondary" style={{ fontSize: 11, marginTop: 2 }}>
                  Actualizado {dayjs(quote.updatedAt).format("DD/MM/YY - HH:mm")} hs
                </Text>
                <Flex
                  align="center"
                  gap={6}
                  style={{ marginTop: 4 }}
                >
                  <Text type="secondary" style={{ fontSize: 11 }}>
                    Válido hasta
                  </Text>
                  <DatePicker
                    size="small"
                    showTime={{ format: "HH:mm" }}
                    format="DD/MM/YY HH:mm"
                    variant="borderless"
                    placeholder="—"
                    style={{ width: 100, padding: 0, fontSize: 11 }}
                    disabled={expiring}
                    allowClear={false}
                    suffixIcon={sellers[0]?.expirationDate ? null : undefined}
                    value={
                      sellers[0]?.expirationDate
                        ? dayjs(sellers[0].expirationDate)
                        : null
                    }
                    onChange={async (date) => {
                      if (sellers.length === 0 || !date) return;
                      closeSearch();
                      for (const s of sellers) {
                        try {
                          await axiosInstance.patch(
                            `${API_URL_QUOTES}/${id}/sellers/${s.sellerCode}/expiration`,
                            { date: date.toISOString() },
                          );
                        } catch {
                          message.error("Error al actualizar validez");
                          return;
                        }
                      }
                      message.success("Validez actualizada");
                      loadQuote();
                    }}
                  />
                  {sellers[0]?.expirationDate && (
                    <SyncOutlined
                      style={{ fontSize: 11, cursor: "pointer", color: "#8c8c8c" }}
                      onClick={() => !expiring && promptForceExpiration()}
                    />
                  )}
                </Flex>
              </Flex>
            </Flex>

            {/* Project row — underlined like input */}
            <Flex
              align="center"
              gap={12}
              style={{ borderBottom: "1px solid #e8e8e8", paddingBottom: 8 }}
            >
              <Text strong style={{ fontSize: 14 }}>
                Proyecto
              </Text>
              {editingName ? (
                <Input
                  size="small"
                  variant="borderless"
                  value={projectName}
                  onChange={(e) => setProjectName(e.target.value)}
                  onPressEnter={handleNameSave}
                  onBlur={handleNameSave}
                  autoFocus
                  style={{ flex: 1, padding: 0 }}
                />
              ) : (
                <Text
                  style={{ cursor: "pointer", flex: 1, color: "#595959" }}
                  onClick={() => setEditingName(true)}
                >
                  {projectName || "Sin nombre"}{" "}
                  <EditOutlined style={{ fontSize: 12, color: "#999" }} />
                </Text>
              )}
            </Flex>

            {/* Store + sellers with operators — 2 columns: left code/name, right operator */}
            <Flex vertical gap={6}>
              {quote.store && (
                <Flex align="center" gap={10}>
                  <Flex align="center" gap={8} style={{ flex: 1 }}>
                    <ShopOutlined style={{ fontSize: 14, color: "#8c8c8c" }} />
                    <Text strong style={{ fontSize: 13 }}>
                      {quote.store.name}
                    </Text>
                  </Flex>
                  <Flex vertical style={{ flex: 1 }} align="flex-start">
                    {storeParticipations.slice(0, 1).map((p: any, i: number) => (
                      <Text
                        key={i}
                        strong
                        style={{ fontSize: 12, color: "#15814b", cursor: "pointer" }}
                        onClick={() => p.contactId && navigate(`/contacts/${p.contactId}`)}
                      >
                        {p.firstname} {p.lastname}
                      </Text>
                    ))}
                  </Flex>
                </Flex>
              )}
              {sellers.map((s: any) => {
                const sellerOps = sellerParticipations.filter(
                  (p: any) => p.participationIdentifier === s.sellerCode
                );
                return (
                  <Flex key={s.sellerCode} align="center" gap={10}>
                    <Flex align="center" gap={8} style={{ flex: 1 }}>
                      <SellerIcon />
                      <Text style={{ fontSize: 13, color: "#595959" }}>
                        {s.sellerCode}
                      </Text>
                    </Flex>
                    <Flex vertical style={{ flex: 1 }} align="flex-start">
                      {sellerOps.slice(0, 1).map((p: any, i: number) => (
                        <Text
                          key={i}
                          strong
                          style={{ fontSize: 12, color: "#15814b", cursor: "pointer" }}
                          onClick={() => p.contactId && navigate(`/contacts/${p.contactId}`)}
                        >
                          {p.firstname} {p.lastname}
                        </Text>
                      ))}
                    </Flex>
                  </Flex>
                );
              })}
            </Flex>
          </Flex>
        </Card>

        {/* Status del presupuesto — stepper */}
        <Card
          size="small"
          style={{ flex: "3 1 480px", borderColor: token.colorBorderSecondary }}
          styles={{ body: { padding: "16px 24px", display: "flex", alignItems: "stretch" } }}
        >
          {(() => {
            // Stepper config.
            //
            // `value` es el target que se manda al hacer click; `matches` son los estados que
            // pintan ese paso como el actual. "Tienda" tiene dos: un presupuesto que armó un
            // operador nace en `store` (sin cliente asignado) y uno que arrancó el cliente en
            // `quoting`. Sin el alias, `store` no matcheaba ningún paso, `activeIdx` quedaba en
            // -1 y el stepper se veía entero en gris.
            const STEPS = [
              {
                value: "quoting",
                matches: ["quoting", "store"],
                label: "Store",
                icon: <ShopOutlined />,
              },
              {
                value: "customer",
                matches: ["customer"],
                label: "Cliente",
                icon: <IdcardOutlined />,
              },
              {
                value: "follow",
                matches: ["follow"],
                label: "Seguimiento",
                icon: <UserOutlined />,
              },
            ] as const;

            const TERMINALS: Record<
              string,
              { label: string; icon: React.ReactNode; color: string }
            > = {
              buy_on: {
                label: "Comprado ON",
                icon: <TrophyOutlined />,
                color: "#15814b",
              },
              bought_assisted: {
                label: "Comprado asistido",
                icon: <TrophyOutlined />,
                color: "#15814b",
              },
              buy_off: {
                label: "Comprado OFF",
                icon: <StopOutlined />,
                color: "#15814b",
              },
              archived: {
                label: "Archivado",
                icon: <FolderOutlined />,
                color: "#595959",
              },
              lost: {
                label: "Perdido",
                icon: <DislikeOutlined />,
                color: "#e74c4c",
              },
            };

            const firstSellerState =
              sellers[0]?.state || prioritizedState || "quoting";
            const currentIdx = STEPS.findIndex((s) =>
              (s.matches as readonly string[]).includes(firstSellerState),
            );
            const isTerminal = firstSellerState in TERMINALS;
            const activeIdx = isTerminal ? 3 : currentIdx;
            const visits = Number(quote?.visits || 0);

            const applyState = (val: string) => {
              // Block forbidden transitions client-side. Backend also validates
              // but stopping here avoids the toast flicker + extra request.
              if (!isAllowedQuoteStateTransition(firstSellerState, val)) {
                message.warning(
                  `No se puede pasar de ${firstSellerState} a ${val}`,
                );
                return;
              }
              sellers.forEach((s: any) => handleStateChange(s.sellerCode, val));
            };

            const canTransitionTo = (val: string) =>
              isAllowedQuoteStateTransition(firstSellerState, val);

            const renderCircle = (
              opts: {
                icon: React.ReactNode;
                active: boolean;
                current?: boolean;
                color: string;
                onClick?: () => void;
              },
            ) => {
              const size = opts.current ? 50 : 40;
              return (
                <div
                  onClick={opts.onClick}
                  style={{
                    width: size,
                    height: size,
                    borderRadius: "50%",
                    border: `${opts.current ? 2.5 : 2}px solid ${
                      opts.active ? opts.color : "#d9d9d9"
                    }`,
                    background: "#fff",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: opts.current ? 20 : 17,
                    color: opts.active ? opts.color : "#bfbfbf",
                    cursor: opts.onClick ? "pointer" : "default",
                    flexShrink: 0,
                    transition: "all 0.15s",
                  }}
                >
                  {opts.icon}
                </div>
              );
            };

            // Header row: title + ⋮ menu
            return (
              <Flex vertical justify="space-between" style={{ width: "100%", flex: 1 }}>
                <Flex align="center" justify="space-between">
                  <Text strong style={{ fontSize: 16 }}>
                    Estado del presupuesto
                  </Text>
                  <Space.Compact size="small">
                    <Button
                      icon={<CopyOutlined />}
                      loading={duplicating}
                      onClick={handleDuplicate}
                    >
                      Duplicar
                    </Button>
                    <Button
                      icon={<DeleteOutlined />}
                      onClick={promptDelete}
                    />
                  </Space.Compact>
                </Flex>

                {/* Stepper row */}
                <Flex align="center" justify="center" style={{ flex: 1, padding: "0 8px", marginTop: 40}}>
                <Flex align="flex-start" style={{ width: "100%" }}>
                  {STEPS.map((step, i) => {
                    const active = i <= activeIdx;
                    const color = "#15814b";
                    // Tienda (quoting/store) is never a manual target. Other
                    // steps are clickable only when reachable from the
                    // current state.
                    const isCurrent = activeIdx === i;
                    const clickable =
                      !isCurrent && canTransitionTo(step.value);
                    return (
                      <React.Fragment key={step.value}>
                        <Flex
                          vertical
                          align="center"
                          gap={6}
                          style={{ flex: "0 0 auto", minWidth: 80 }}
                        >
                          <div
                            style={{
                              height: 50,
                              display: "flex",
                              alignItems: "center",
                            }}
                          >
                            {renderCircle({
                              icon: step.icon,
                              active,
                              current: isCurrent,
                              color,
                              onClick: clickable
                                ? () => applyState(step.value)
                                : undefined,
                            })}
                          </div>
                          <Text
                            style={{
                              fontSize: 13,
                              color: active ? "#262626" : "#8c8c8c",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {step.label}
                          </Text>
                          {activeIdx === i && visits > 0 && (
                            <Text
                              type="secondary"
                              style={{ fontSize: 11, marginTop: -4 }}
                            >
                              Viewed x {visits}
                            </Text>
                          )}
                        </Flex>
                        {/* Connector line */}
                        <div
                          style={{
                            flex: 1,
                            height: 2,
                            background: i < activeIdx ? color : "#e8e8e8",
                            marginTop: 24,
                          }}
                        />
                      </React.Fragment>
                    );
                  })}

                  {/* Terminal step (4th position) */}
                  <Flex
                    vertical
                    align="center"
                    gap={6}
                    style={{ flex: "0 0 auto", minWidth: 96 }}
                  >
                    <Dropdown
                      trigger={["click"]}
                      menu={{
                        // buy_on / bought_assisted are auto-only (set on order
                        // confirmation), so never shown as a manual option. The
                        // rest is filtered by allowed transitions from the
                        // current state.
                        items: Object.entries(TERMINALS)
                          .filter(
                            ([val]) =>
                              val !== "buy_on" &&
                              val !== "bought_assisted" &&
                              canTransitionTo(val),
                          )
                          .map(([val, meta]) => ({
                            key: val,
                            label: (
                              <Flex align="center" gap={8}>
                                <span style={{ color: meta.color }}>
                                  {meta.icon}
                                </span>
                                {meta.label}
                              </Flex>
                            ),
                            onClick: () => applyState(val),
                          })),
                      }}
                    >
                      <div
                        style={{
                          height: 50,
                          display: "flex",
                          alignItems: "center",
                          cursor: "pointer",
                        }}
                      >
                        {isTerminal
                          ? renderCircle({
                              icon: TERMINALS[firstSellerState].icon,
                              active: true,
                              current: true,
                              color: TERMINALS[firstSellerState].color,
                            })
                          : renderCircle({
                              icon: <DownOutlined style={{ fontSize: 14 }} />,
                              active: false,
                              color: "#bfbfbf",
                            })}
                      </div>
                    </Dropdown>
                    <Text
                      style={{
                        fontSize: 13,
                        color: isTerminal ? "#262626" : "#8c8c8c",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {isTerminal ? TERMINALS[firstSellerState].label : "—"}
                    </Text>
                    {isTerminal && purchasedAt && (
                      <Text style={{ fontSize: 11, marginTop: -4, color: "#262626" }}>
                        {dayjs(purchasedAt).format("DD/MM/YY - HH:mm")} hs
                      </Text>
                    )}
                    {isTerminal && visits > 0 && (
                      <Text
                        type="secondary"
                        style={{ fontSize: 11, marginTop: -4 }}
                      >
                        Viewed x {visits}
                      </Text>
                    )}
                  </Flex>
                </Flex>
                </Flex>

              </Flex>
            );
          })()}
        </Card>
      </Flex>

      {/* ===== ADDRESS SECTIONS ===== */}
      <Flex gap={16} align="stretch" style={{ marginBottom: 16 }}>
        {/* Delivery address */}
        <Flex vertical style={{ flex: "2 1 0%" }}>
        <Card
          size="small"
          style={{
            overflow: "hidden",
            position: "relative",
            height: "100%",
            borderColor:
              submitAttempted && quoteLevelErrors.shipping ? ERROR_RED : token.colorBorderSecondary,
          }}
          styles={{
            body: {
              padding: 0,
              height: "100%",
              display: "flex",
              flexDirection: "column",
              justifyContent: "flex-end",
            },
          }}
        >
          {/* Map background */}
          <div
            style={{
              position: "absolute",
              inset: 0,
              background: `url(${mapSrc}) center/cover no-repeat`,
              opacity: 0.35,
            }}
          />
          {/* Content overlay */}
          <Flex
            align="center"
            gap={16}
            style={{
              padding: "30px 16px",
              position: "relative",
              zIndex: 1,
              marginTop: "auto",
            }}
          >
            {/* Truck icon */}
            <svg width="56" height="56" viewBox="0 0 60 60" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ flexShrink: 0 }}>
              <rect x="6.25" y="13.75" width="29" height="25" fill="#E6F4FF"/>
              <path d="M37.25 25.75H47.25C50.5637 25.75 53.25 28.4363 53.25 31.75V37.75H40.25C38.5931 37.75 37.25 36.4069 37.25 34.75V25.75Z" fill="#E6F4FF"/>
              <circle cx="41.5" cy="41.5" r="4.5" fill="#E6F4FF"/>
              <circle cx="14.5" cy="40.5" r="4.5" fill="#E6F4FF"/>
              <path fillRule="evenodd" clipRule="evenodd" d="M35.875 11C36.9105 11 37.75 11.8395 37.75 12.875V22.25H47.9931C48.3027 22.25 48.6074 22.3267 48.8802 22.4732C49.1529 22.6196 49.3851 22.8314 49.5561 23.0895L56.1881 33.1C56.3915 33.407 56.5 33.7672 56.5 34.1355V39.125C56.5 40.1605 55.6605 41 54.625 41H49C49 45.1421 45.6421 48.5 41.5 48.5C37.3579 48.5 34 45.1421 34 41H22.75C22.75 45.1421 19.3921 48.5 15.25 48.5C11.1079 48.5 7.75 45.1421 7.75 41H5.875C4.83947 41 4 40.1605 4 39.125V12.875C4 11.8395 4.83947 11 5.875 11H35.875ZM15.25 37.25C13.1789 37.25 11.5 38.9289 11.5 41C11.5 43.0711 13.1789 44.75 15.25 44.75L15.312 44.7495C17.3545 44.7164 19 43.0504 19 41C19 38.9289 17.3211 37.25 15.25 37.25ZM41.5 37.25C39.4289 37.25 37.75 38.9289 37.75 41C37.75 43.0711 39.4289 44.75 41.5 44.75L41.562 44.7495C43.6045 44.7164 45.25 43.0504 45.25 41C45.25 38.9289 43.5711 37.25 41.5 37.25ZM34 14.75H7.75V37.25H8.75336C10.0501 35.0083 12.4739 33.5 15.25 33.5C18.0261 33.5 20.4499 35.0083 21.7466 37.25H34V14.75ZM46.9861 26H37.75V34.5034C38.8533 33.8653 40.134 33.5 41.5 33.5C44.2761 33.5 46.6999 35.0083 47.9966 37.25H52.75V34.7002L46.9861 26ZM29.5469 26C29.9352 26 30.25 26.3148 30.25 26.7031V29.0469C30.25 29.4352 29.9352 29.75 29.5469 29.75H19.7031C19.3148 29.75 19 29.4352 19 29.0469V26.7031C19 26.3148 19.3148 26 19.7031 26H29.5469ZM18.2969 18.5C18.6852 18.5 19 18.8148 19 19.2031V21.5469C19 21.9352 18.6852 22.25 18.2969 22.25H12.2031C11.8148 22.25 11.5 21.9352 11.5 21.5469V19.2031C11.5 18.8148 11.8148 18.5 12.2031 18.5H18.2969Z" fill="#003EB3"/>
            </svg>
            <Flex vertical style={{ flex: 1 }}>
              <Flex align="center" gap={6} style={{ marginBottom: 4 }}>
                <Text strong style={{ fontSize: 16 }}>
                  Retiro o entrega
                </Text>
                {ownerUserId && (
                  <span
                    onClick={() => setAddressBookType("shipping")}
                    style={{ cursor: "pointer", display: "inline-flex", lineHeight: 0 }}
                  >
                    <svg width="24" height="26" viewBox="0 0 24 26" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <g filter="url(#filter_shipping)">
                        <mask id="mask_shipping" fill="white"><path d="M0 4C0 1.79086 1.79086 0 4 0H20C22.2091 0 24 1.79086 24 4V20C24 22.2091 22.2091 24 20 24H4C1.79086 24 0 22.2091 0 20V4Z"/></mask>
                        <path d="M0 4C0 1.79086 1.79086 0 4 0H20C22.2091 0 24 1.79086 24 4V20C24 22.2091 22.2091 24 20 24H4C1.79086 24 0 22.2091 0 20V4Z" fill="white" shapeRendering="crispEdges"/>
                        <path d="M4 0V1H20V0V-1H4V0ZM24 4H23V20H24H25V4H24ZM20 24V23H4V24V25H20V24ZM0 20H1V4H0H-1V20H0ZM4 24V23C2.34315 23 1 21.6569 1 20H0H-1C-1 22.7614 1.23858 25 4 25V24ZM24 20H23C23 21.6569 21.6569 23 20 23V24V25C22.7614 25 25 22.7614 25 20H24ZM20 0V1C21.6569 1 23 2.34315 23 4H24H25C25 1.23858 22.7614 -1 20 -1V0ZM4 0V-1C1.23858 -1 -1 1.23858 -1 4H0H1C1 2.34315 2.34315 1 4 1V0Z" fill="#D9D9D9" mask="url(#mask_shipping)"/>
                        <path d="M15.1178 15.7647L15.6069 8.85822C15.6114 8.79575 15.6024 8.73306 15.5805 8.67439C15.5586 8.61572 15.5243 8.56243 15.4801 8.51815C15.4358 8.47387 15.3825 8.43963 15.3238 8.41774C15.2652 8.39585 15.2025 8.38682 15.14 8.39128L8.23452 8.88142C8.21369 8.88287 8.1937 8.89029 8.17696 8.90278C8.16021 8.91526 8.14741 8.9323 8.14008 8.95186C8.13274 8.97142 8.13118 8.99268 8.13559 9.0131C8.13999 9.03352 8.15017 9.05224 8.16492 9.06704L8.94798 9.8501C8.99245 9.89457 9.05433 9.91777 9.11813 9.91391L13.6734 9.59101L8.22969 15.0348C8.18715 15.0773 8.18715 15.1469 8.22969 15.1894L8.80974 15.7695C8.85228 15.812 8.92188 15.812 8.96442 15.7695L14.4082 10.3257L14.0853 14.8811C14.0804 14.9439 14.1036 15.0058 14.1491 15.0512L14.9322 15.8343C14.9979 15.9 15.111 15.8584 15.1178 15.7647Z" fill="black" fillOpacity="0.88"/>
                      </g>
                      <defs>
                        <filter id="filter_shipping" x="0" y="0" width="24" height="26" filterUnits="userSpaceOnUse" colorInterpolationFilters="sRGB">
                          <feFlood floodOpacity="0" result="BackgroundImageFix"/>
                          <feColorMatrix in="SourceAlpha" type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0" result="hardAlpha"/>
                          <feOffset dy="2"/>
                          <feComposite in2="hardAlpha" operator="out"/>
                          <feColorMatrix type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.02 0"/>
                          <feBlend mode="normal" in2="BackgroundImageFix" result="effect1_dropShadow_shipping"/>
                          <feBlend mode="normal" in="SourceGraphic" in2="effect1_dropShadow_shipping" result="shape"/>
                        </filter>
                      </defs>
                    </svg>
                  </span>
                )}
              </Flex>
              <Flex gap={8} align="center">
                <div style={{ flex: 1, minWidth: 0 }}>
                  {isMapsLoaded ? (
                    <Autocomplete onLoad={onInlineAutocompleteLoad}>
                      <Input
                        value={inlineAddressValue}
                        onChange={(e) => setInlineAddressValue(e.target.value)}
                        placeholder="Escribe la dirección"
                        disabled={inlineAddressSaving}
                        style={{
                          background: "#fff",
                          color: "#333",
                          borderColor: "#d9d9d9",
                        }}
                      />
                    </Autocomplete>
                  ) : (
                    <Input
                      value={addressText}
                      disabled
                      style={{
                        background: "#fff",
                        color: "#333",
                        borderColor: "#d9d9d9",
                        cursor: "default",
                      }}
                    />
                  )}
                </div>
                <Button
                  type="default"
                  icon={<EditOutlined />}
                  size="small"
                  onClick={() => setDeliveryModalOpen(true)}
                  style={{ flexShrink: 0 }}
                />
              </Flex>
            </Flex>
          </Flex>
        </Card>
          {submitAttempted && quoteLevelErrors.shipping && (
            <Text
              data-error="true"
              style={{ color: ERROR_RED, fontSize: 12, marginTop: 4 }}
            >
              {t(quoteLevelErrors.shipping)}
            </Text>
          )}
        </Flex>

        {/* Billing */}
        <Flex vertical style={{ flex: 1 }}>
        <Card
          size="small"
          style={{
            height: "100%",
            borderColor:
              submitAttempted && quoteLevelErrors.billing ? ERROR_RED : token.colorBorderSecondary,
          }}
          styles={{ body: { padding: 16 } }}
        >
          <Flex align="start" gap={16} style={{ marginBottom: 8 }}>
            <FileTextOutlined style={{ color: "#ff6b35", fontSize: 50, flexShrink: 0 }} />
            <Flex vertical align="end" style={{ flex: 1, minWidth: 0 }}>
              <Flex align="center" gap={6}>
                <Text strong style={{ fontSize: 16 }}>Facturación</Text>
                {ownerUserId && (
                  <span
                    onClick={() => setAddressBookType("billing")}
                    style={{ cursor: "pointer", display: "inline-flex", lineHeight: 0 }}
                  >
                    <svg width="24" height="26" viewBox="0 0 24 26" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <g filter="url(#filter_billing)">
                        <mask id="mask_billing" fill="white"><path d="M0 4C0 1.79086 1.79086 0 4 0H20C22.2091 0 24 1.79086 24 4V20C24 22.2091 22.2091 24 20 24H4C1.79086 24 0 22.2091 0 20V4Z"/></mask>
                        <path d="M0 4C0 1.79086 1.79086 0 4 0H20C22.2091 0 24 1.79086 24 4V20C24 22.2091 22.2091 24 20 24H4C1.79086 24 0 22.2091 0 20V4Z" fill="white" shapeRendering="crispEdges"/>
                        <path d="M4 0V1H20V0V-1H4V0ZM24 4H23V20H24H25V4H24ZM20 24V23H4V24V25H20V24ZM0 20H1V4H0H-1V20H0ZM4 24V23C2.34315 23 1 21.6569 1 20H0H-1C-1 22.7614 1.23858 25 4 25V24ZM24 20H23C23 21.6569 21.6569 23 20 23V24V25C22.7614 25 25 22.7614 25 20H24ZM20 0V1C21.6569 1 23 2.34315 23 4H24H25C25 1.23858 22.7614 -1 20 -1V0ZM4 0V-1C1.23858 -1 -1 1.23858 -1 4H0H1C1 2.34315 2.34315 1 4 1V0Z" fill="#D9D9D9" mask="url(#mask_billing)"/>
                        <path d="M15.1178 15.7647L15.6069 8.85822C15.6114 8.79575 15.6024 8.73306 15.5805 8.67439C15.5586 8.61572 15.5243 8.56243 15.4801 8.51815C15.4358 8.47387 15.3825 8.43963 15.3238 8.41774C15.2652 8.39585 15.2025 8.38682 15.14 8.39128L8.23452 8.88142C8.21369 8.88287 8.1937 8.89029 8.17696 8.90278C8.16021 8.91526 8.14741 8.9323 8.14008 8.95186C8.13274 8.97142 8.13118 8.99268 8.13559 9.0131C8.13999 9.03352 8.15017 9.05224 8.16492 9.06704L8.94798 9.8501C8.99245 9.89457 9.05433 9.91777 9.11813 9.91391L13.6734 9.59101L8.22969 15.0348C8.18715 15.0773 8.18715 15.1469 8.22969 15.1894L8.80974 15.7695C8.85228 15.812 8.92188 15.812 8.96442 15.7695L14.4082 10.3257L14.0853 14.8811C14.0804 14.9439 14.1036 15.0058 14.1491 15.0512L14.9322 15.8343C14.9979 15.9 15.111 15.8584 15.1178 15.7647Z" fill="black" fillOpacity="0.88"/>
                      </g>
                      <defs>
                        <filter id="filter_billing" x="0" y="0" width="24" height="26" filterUnits="userSpaceOnUse" colorInterpolationFilters="sRGB">
                          <feFlood floodOpacity="0" result="BackgroundImageFix"/>
                          <feColorMatrix in="SourceAlpha" type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0" result="hardAlpha"/>
                          <feOffset dy="2"/>
                          <feComposite in2="hardAlpha" operator="out"/>
                          <feColorMatrix type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.02 0"/>
                          <feBlend mode="normal" in2="BackgroundImageFix" result="effect1_dropShadow_billing"/>
                          <feBlend mode="normal" in="SourceGraphic" in2="effect1_dropShadow_billing" result="shape"/>
                        </filter>
                      </defs>
                    </svg>
                  </span>
                )}
              </Flex>
              {billingAddr?.billingType && (
                <Text style={{ fontSize: 12, marginTop: 2 }}>
                  Tipo de factura: <Text strong>"{billingLetter}"</Text>
                </Text>
              )}
            </Flex>
          </Flex>
          <Divider style={{ margin: "8px 0 12px" }} />
          <Flex justify="space-between" align="center" gap={12}>
            <Flex vertical style={{ flex: 1, minWidth: 0 }}>
              {billingAddr ? (
                <>
                  <Text strong style={{ fontSize: 14, textTransform: "uppercase" }}>
                    {billingCompanyLine || "—"}
                  </Text>
                  {billingAddressLine && (
                    <Text type="secondary" style={{ fontSize: 13, marginTop: 2 }}>
                      {billingAddressLine}
                    </Text>
                  )}
                </>
              ) : (
                <Text type="secondary" style={{ fontSize: 13 }}>
                  Sin datos de facturación
                </Text>
              )}
            </Flex>
            <Button
              type="default"
              icon={<EditOutlined />}
              size="small"
              onClick={() => setBillingModalOpen(true)}
              style={{ flexShrink: 0 }}
            />
          </Flex>
        </Card>
          {submitAttempted && quoteLevelErrors.billing && (
            <Text
              data-error="true"
              style={{ color: ERROR_RED, fontSize: 12, marginTop: 4 }}
            >
              {t(quoteLevelErrors.billing)}
            </Text>
          )}
        </Flex>
      </Flex>

      {/* ===== PRODUCT SEARCH ===== */}
      <Card size="small" style={{ marginBottom: 16, borderColor: token.colorBorderSecondary }} styles={{ body: { padding: "12px 16px" } }}>
        <Text strong style={{ display: "block", marginBottom: 8, fontSize: 14 }}>
          Busca y agregá productos
        </Text>
        <Input
          placeholder="Busca por Nombre, SKU de XCONS o del Seller"
          suffix={
            searchLoading ? (
              <Spin size="small" />
            ) : (
              <SearchOutlined
                style={{ color: "#999", cursor: "pointer" }}
                onClick={handleProductSearch}
              />
            )
          }
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          onPressEnter={handleProductSearch}
          allowClear
          onClear={() => {
            setSearchOpen(false);
            setSearchResults([]);
            setAddQtyMap({});
          }}
        />

        {searchOpen && searchResults.length > 0 && (
          <div
            style={{
              marginTop: 8,
              border: "1px solid #f0f0f0",
              borderRadius: 8,
              maxHeight: 420,
              overflowY: "auto",
            }}
          >
            {searchResults.map((p: any) => {
              const vendor = selectedVendor[p.objectID];
              const vendorPrice = vendor ? p.priceByVendor?.[vendor] : null;
              const displayPrice = vendorPrice || p.price || 0;
              const hasOriginal = p.originalPrice && p.originalPrice !== formatPrice(displayPrice);
              const qty = addQtyMap[p.objectID] || 1;
              const stock = vendor ? p.stockByVendor?.[vendor] : null;

              // Mirror Magento `renderOptimizerResults` (custom.js:2264-2283):
              // when the SKU has a secondary unit of sale, show its per-alt-unit
              // price below the main price. Calculated on the final price (i.e.
              // on the special when there's an offer).
              const innerVal = Number(p.inner);
              let altLabel = p.unitOfSaleAlt != null ? String(p.unitOfSaleAlt).trim() : "";
              const altLower = altLabel.toLowerCase();
              if (!altLabel || altLabel === "false" || altLabel === "0" || altLower === "null" || altLower === "undefined") {
                altLabel = "";
              }
              const showAltPrice =
                isFinite(displayPrice) && isFinite(innerVal) && innerVal > 0 && !!altLabel;
              const altUnitPrice = showAltPrice ? displayPrice / innerVal : null;

              return (
                <Flex
                  key={p.objectID}
                  align="center"
                  gap={12}
                  style={{
                    padding: "10px 12px",
                    borderBottom: "1px solid #f5f5f5",
                  }}
                >
                  {/* Image */}
                  {p.image ? (
                    <img
                      src={p.image}
                      alt=""
                      style={{
                        width: 56,
                        height: 56,
                        objectFit: "contain",
                        borderRadius: 4,
                        border: "1px solid #f0f0f0",
                        flexShrink: 0,
                      }}
                    />
                  ) : (
                    <div
                      style={{
                        width: 56,
                        height: 56,
                        borderRadius: 4,
                        background: "#f5f5f5",
                        border: "1px solid #f0f0f0",
                        flexShrink: 0,
                      }}
                    />
                  )}

                  {/* Name + manufacturer + vendor tags */}
                  <Flex vertical style={{ flex: 1, minWidth: 0 }}>
                    <Text strong ellipsis style={{ fontSize: 13 }}>
                      {p.name}
                    </Text>
                    {p.manufacturer && (
                      <Text type="secondary" style={{ fontSize: 11 }}>
                        {p.manufacturer}
                      </Text>
                    )}
                    <Flex gap={4} wrap="wrap" style={{ marginTop: 3 }}>
                      {(p.vendors || []).map((v: string) => (
                        <Tag
                          key={v}
                          style={{
                            fontSize: 10,
                            lineHeight: "16px",
                            margin: 0,
                            cursor: "pointer",
                            borderStyle: vendor === v ? "solid" : "dashed",
                          }}
                          color={vendor === v ? "green" : undefined}
                          onClick={() =>
                            setSelectedVendor((prev) => ({
                              ...prev,
                              [p.objectID]: v,
                            }))
                          }
                        >
                          ✦ {v}
                        </Tag>
                      ))}
                    </Flex>
                  </Flex>

                  {/* Price */}
                  <Flex vertical align="flex-end" style={{ flexShrink: 0, minWidth: 90 }}>
                    {hasOriginal && (
                      <Text
                        type="secondary"
                        style={{ fontSize: 11, textDecoration: "line-through" }}
                      >
                        {p.originalPrice}
                      </Text>
                    )}
                    <Text strong style={{ fontSize: 14 }}>
                      {formatPrice(displayPrice)}
                    </Text>
                    {showAltPrice && (
                      <Text type="secondary" style={{ fontSize: 11 }}>
                        {formatPrice(altUnitPrice!)} x {altLabel}
                      </Text>
                    )}
                  </Flex>

                  {/* Qty */}
                  <InputNumber
                    size="small"
                    min={1}
                    value={qty}
                    onChange={(val) =>
                      setAddQtyMap((prev) => ({ ...prev, [p.objectID]: val || 1 }))
                    }
                    {...selectAllOnFocus}
                    style={{ width: 56 }}
                  />

                  {/* Stock */}
                  <Flex vertical align="center" style={{ width: 80, flexShrink: 0 }}>
                    {stock != null && stock > 0 ? (
                      <Text style={{ fontSize: 11, color: "rgba(70,140,105,1)", textAlign: "center" }}>
                        Stock disponible
                      </Text>
                    ) : vendor ? (
                      <Text style={{ fontSize: 11, color: "#f5222d", textAlign: "center" }}>
                        Sin stock
                      </Text>
                    ) : (
                      <Text type="secondary" style={{ fontSize: 11, textAlign: "center" }}>
                        Elegí seller
                      </Text>
                    )}
                  </Flex>

                  {/* Add button */}
                  <Button
                    type="primary"
                    size="small"
                    disabled={!vendor}
                    onClick={() => handleAddProduct(p)}
                    style={{ flexShrink: 0 }}
                  >
                    Agregar
                  </Button>
                </Flex>
              );
            })}
          </div>
        )}

        {searchOpen && searchResults.length === 0 && !searchLoading && (
          <Text
            type="secondary"
            style={{ display: "block", padding: "12px 0", fontSize: 12, textAlign: "center" }}
          >
            No se encontraron productos
          </Text>
        )}
      </Card>

      {/* ===== SELLER BLOCKS ===== */}
      {sellers.length === 0 ? (
        <Card
          style={{ textAlign: "center", padding: "60px 20px", marginBottom: 16 }}
        >
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description={
              <Flex vertical align="center" gap={8}>
                <Title level={4} style={{ margin: 0 }}>
                  El presupuesto está vacío
                </Title>
                <Text type="secondary">
                  Parece que todavía no agregaste ningún producto
                </Text>
              </Flex>
            }
          />
        </Card>
      ) : (
        sellers.map((seller: any) => (
          <SellerBlock
            key={seller.sellerCode}
            ref={(el) => { sellerRefs.current[seller.sellerCode] = el; }}
            quoteId={Number(id)}
            seller={seller}
            customerName={customerName || undefined}
            onStateChange={handleStateChange}
            onRefresh={loadQuote}
            quoteLevelErrors={quoteLevelErrors}
            onSubmitAttempt={registerSubmitAttempt}
            storeLink={quote.store?.link || ""}
            onNonAddAction={closeSearch}
            readOnly={quoteReadOnly}
          />
        ))
      )}

      {/* ===== HISTORIAL (OpenSpec 008 — nuevo modelo/diseño) ===== */}
      <Card
        size="small"
        title={
          <Flex align="center" gap={24} style={{ width: "100%" }}>
            <Flex gap={4} align="center" style={{ flexShrink: 0 }}>
              <HistoryOutlined style={{ fontSize: 16 }} />
              <Text style={{ fontSize: 14, fontWeight: 600 }}>
                {t("quotes.history.title")}
              </Text>
            </Flex>
            <Flex flex={1} gap={16} align="center">
              <Input.Search
                size="small"
                allowClear
                placeholder={t("quotes.history.search.placeholder")}
                value={historySearch}
                onChange={(e) => setHistorySearch(e.target.value)}
                style={{ flex: 1 }}
              />
              <Button
                size="small"
                shape="round"
                icon={<FilterOutlined style={{ fontSize: 14 }} />}
              />
            </Flex>
          </Flex>
        }
        style={{ marginBottom: 16, borderColor: token.colorBorderSecondary }}
      >
        <HistorySection module="quote" entityId={Number(id)} externalQ={historyQ} />
      </Card>

      {/* ===== MODALS ===== */}
      <DeliveryAddressModal
        open={deliveryModalOpen}
        onClose={() => setDeliveryModalOpen(false)}
        quoteId={Number(id)}
        shippingAddress={quoteAddresses.shipping}
        currentAddress={{
          city: quote.city,
          region: quote.region,
          latitude: quote.latitude,
          longitude: quote.longitude,
        }}
        userId={ownerUserId}
        storeCode={quote.store?.code || null}
        onSave={() => {
          setDeliveryModalOpen(false);
          loadQuote();
        }}
        ownerContact={ownerUserId ? { name: customerName || undefined, phone: customerWhatsapp || undefined, vatId: ownerContactVatId } : undefined}
        onOpenAgenda={ownerUserId ? () => { setAddressBookType("shipping"); } : undefined}
      />
      <BillingDataModal
        open={billingModalOpen}
        onClose={() => setBillingModalOpen(false)}
        quoteId={Number(id)}
        billingAddress={quoteAddresses.billing}
        userId={ownerUserId}
        storeCode={quote.store?.code || null}
        onSave={() => {
          setBillingModalOpen(false);
          loadQuote();
        }}
        onOpenAgenda={ownerUserId ? () => { setAddressBookType("billing"); } : undefined}
      />
      {ownerUserId && (
        <QuoteAddressBookPopup
          open={addressBookType !== null}
          type={addressBookType || "shipping"}
          quoteId={Number(id)}
          userId={ownerUserId}
          quoteLabel={id}
          storeCode={quote.store?.code || null}
          onClose={() => setAddressBookType(null)}
          onSaved={() => {
            setAddressBookType(null);
            loadQuote();
          }}
        />
      )}

{/* ===== ADD OWNER DRAWER ===== */}
      <Drawer
        title={t("quotes.drawer.add.owner")}
        open={ownerModalOpen}
        onClose={() => setOwnerModalOpen(false)}
        width={480}
        destroyOnClose
      >
        <SmartSelectContact
          handleCancel={() => setOwnerModalOpen(false)}
          onSaveContact={async () => {}}
          store={
            quote.store
              ? { code: quote.store.code, name: quote.store.name, link: "store" }
              : undefined
          }
          refetch={() => {
            setOwnerModalOpen(false);
            loadQuote();
          }}
          sectionType="contact"
          contactId={Number(id)}
          contactStores={{ storeCode: quote.store?.code || "" }}
          participants={quote.participations || []}
          permissionCode="quote"
        />
      </Drawer>

      {/* Compartir por WhatsApp (N6): elegido el canal, se abre Conversaciones en un tab nuevo con
          todo resuelto — canal, contacto y el mensaje ya escrito. Sólo falta apretar enviar. */}
    </div>
  );
};
